import React, { useCallback, useState, useEffect } from 'react';
import { path, propOr, pathOr, isEmpty, includes, equals, sortBy, compose, reverse, prop, isNil, find } from 'ramda';
import moment from 'moment';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { GET_JOBS, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import { UPDATE_JOB_STATUS, REFRESH_JOB, ARCHIVE_JOB, UNARCHIVE_JOB } from '../apollo/mutation';
import Footer from './Footer';
import {
  Page,
  Layout,
  Button,
  Card,
  Heading,
  Tooltip,
  TextStyle,
  Filters,
  ResourceList,
  EmptyState,
  AppProvider,
  Pagination,
  Frame,
  Link,
  Banner,
  Badge,
  ChoiceList,
  Subheading,
  Modal,
  TextContainer,
  Spinner,
  Toast,
  Tabs,
  Icon,
} from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import { LinkMinor, ViewMajorMonotone } from '@shopify/polaris-icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Loader from 'react-loader-spinner';

import { noJobsFoundImgUrl } from '../../utils/noJobsFoundImgUrl';

const img = 'https://cdn.shopify.com/s/files/1/0757/9955/files/empty-state.svg';

const statusIndex = ['open', 'closed', ['open', 'closed']];

const GROWTH_SUBSCRIPTION_NUM_JOBS = 5;
const DEFAULT_NUM_JOBS = 3;

const categories = [
  { label: 'Select Category', value: '' },
  { label: 'Accounting', value: 'accounting' },
  { label: 'Administrative & Office', value: 'administrative and office' },
  { label: 'Cleaning & Facilities', value: 'cleaning and facilities' },
  { label: 'Customer Service', value: 'customer service' },
  { label: 'Design', value: 'design' },
  { label: 'Education', value: 'education' },
  { label: 'Engineering', value: 'engineering' },
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Human Resources', value: 'human resources' },
  {
    label: 'Installation, Maintenance & Repair',
    value: 'installation, mantenance and repair',
  },
  { label: 'Legal', value: 'legal' },
  { label: 'Management', value: 'managment' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Merchandising', value: 'merchandising' },
  { label: 'Personal Care Services', value: 'personal care services' },
  { label: 'Hospitality', value: 'hospitality' },
  { label: 'Retail', value: 'retail' },
  { label: 'Sales', value: 'sales' },
  {
    label: 'Social Services & Nonprofit',
    value: 'social services and nonprofit',
  },
  { label: 'Fitness', value: 'fitness' },
  { label: 'Logistics', value: 'logistics' },
  { label: 'Technology', value: 'technology' },
  { label: 'Production', value: 'production' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Food Production', value: 'food production' },
  { label: 'eCommerce', value: 'eCommerce' },
  { label: 'Finance', value: 'finance' },
  { label: 'NoCode', value: 'noCode' },
  { label: 'Operations', value: 'operations' },
  { label: 'Public Relations', value: 'public relations' },
];

const compareJobArrays = (arr1, arr2) => {
  if (!!arr1 && !!arr1.map && !!arr2 && !!arr2.map) {
    const arr1Ids = arr1.map(({ id }) => id);
    const arr2Ids = arr2.map(({ id }) => id);
    return JSON.stringify(arr1Ids) !== JSON.stringify(arr2Ids);
  }
  return JSON.stringify(arr1) !== JSON.stringify(arr2);
};

const tabs = [
  {
    id: 'open',
    content: 'Open',
    accessibilityLabel: 'Open',
    panelID: 'open-jobs-content',
  },
  {
    id: 'closed',
    content: 'Closed',
    panelID: 'closed-jobs-content',
  },
  {
    id: 'all',
    content: 'All',
    panelID: 'all-jobs-content',
  },
  {
    id: 'archived',
    content: 'Archived',
    panelID: 'archived-jobs-content',
  },
];

const ROWS_PER_PAGE = 15;

const JobList = (props) => {
  const { shopifyToken, shopifyDomain, careerPageLink } = props;

  const [updateJobStatus] = useMutation(UPDATE_JOB_STATUS);
  const [refreshJob] = useMutation(REFRESH_JOB);
  const [archiveJob] = useMutation(ARCHIVE_JOB);
  const [unarchiveJob] = useMutation(UNARCHIVE_JOB);
  const [isLoading, setLoading] = useState(false);
  const [currentJob, setCurrentJob] = useState({});
  const [queryValue, setQueryValue] = useState(null);
  const [filterQueryJobs, setFilterQueryJobs] = useState(null);
  const [selected, setSelected] = useState(0);
  const [active, setActive] = useState(false);
  const [archiveModalActive, setArchiveModalActive] = useState(false);
  const [isArchivedTab, setIsArchivedTab] = useState(false);
  const [jobStatusFilter, setJobStatusFilter] = useState('open');
  const [category2, setCategory2] = useState(null);
  const [department2, setDepartment2] = useState(null);
  const [location, setLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [page, setPage] = useState(0);
  const [successToast, setSuccessToast] = useState({
    status: false,
    message: '',
  });
  const [sortValue, setSortValue] = useState('DATE_MODIFIED_DESC');
  const [tabJobs, setTabJobs] = useState([]);
  const handleModal = useCallback(() => setActive(!active), [active]);
  const handleArchiveModal = useCallback(() => setArchiveModalActive(!archiveModalActive), [archiveModalActive]);
  const toggleActive = useCallback(() => setSuccessToast(({ status, message }) => ({ status: !status, message })), []);

  const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

  const handleQueryValueRemove = () => {
    setQueryValue(null);
    if (isNilOrEmpty(category2)) return setFilterQueryJobs(null);

    const valueLowerCase = category2 ? category2.join(' ').toLowerCase() : '';
    setFilterQueryJobs(tabJobs.filter((item) => includes(item.category.toLowerCase(), valueLowerCase)));
  };

  const handleCurrentJob = (id) => {
    const job1 = jobs.filter((j) => j.id == id);
    setActive(!active);
    setCurrentJob(mapRemoteJob(job1[0]));
  };

  const mapRemoteJob = (item) => {
    const { isRemote } = { ...item };
    return isRemote ? { ...item, location: 'Remote' } : item;
  };

  const handleTabChange = (selectedTabIndex) => {
    setFilterQueryJobs(null);
    handleQueryValueRemove();

    const tabId = tabs[selectedTabIndex]?.id;

    if (tabId === 'archived') {
      setIsArchivedTab(true);
      setJobStatusFilter(undefined);
    } else if (tabId === 'all') {
      setIsArchivedTab(false);
      setTabJobs(jobs.map(mapRemoteJob));
      setJobStatusFilter(undefined);
    } else {
      setIsArchivedTab(false);
      setTabJobs(jobs.filter((c) => statusIndex[selectedTabIndex] == c.status).map(mapRemoteJob));
      setJobStatusFilter(tabId);
    }

    setSelected(selectedTabIndex);
    setPage(0);
  };

  const { loading, data, refetch } = useQuery(GET_JOBS, {
    variables: { shopifyToken, templates: false, status: jobStatusFilter, archived: isArchivedTab ? true : false },
    fetchPolicy: 'network-only',
  });

  const { data: currentSubscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, {
    variables: { shopifyToken },
  });

  const shop = propOr({}, 'shop')(currentSubscriptionData);
  const allAccess = propOr(null, 'allAccess')(shop);
  const hasJobs = propOr(false, 'hasJobs')(shop);
  const currentSubscription = propOr({}, 'currentSubscription')(shop);
  const subscriptionType = pathOr('', ['shop', 'currentSubscription', 'subscriptionType'])(currentSubscriptionData);
  const isGrowthSubscription = subscriptionType === 'Growth';
  const isBusinessSubscription = subscriptionType === 'Business';
  const subscriptionPlan = currentSubscription.subscriptionPlan || {
    number_jobs: isGrowthSubscription ? GROWTH_SUBSCRIPTION_NUM_JOBS : DEFAULT_NUM_JOBS,
  };

  const { careerPortal = {} } = { ...shop };
  const { departments = [] } = { ...careerPortal };

  const handleFiltersQueryChange = (value) => {
    setQueryValue(value);

    if (isNilOrEmpty(value) && isNilOrEmpty(category2)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(category2)) {
      return setFilterQueryJobs(filterQueryJobs);
    }

    const valueLowerCase = category2 ? category2.join(' ').toLowerCase() : '';

    setFilterQueryJobs(
      tabJobs.filter(function (item) {
        const searchedTextCondition = item.title.toLowerCase().search(value.toLowerCase()) !== -1;
        const bothConditions = searchedTextCondition && includes(item.category.toLowerCase(), valueLowerCase);

        return isNilOrEmpty(category2) ? searchedTextCondition : bothConditions;
      })
    );
  };

  const handleCategoryRemove = () => {
    setCategory2(null);

    if (queryValue) {
      return setFilterQueryJobs(tabJobs.filter((item) => item.title.toLowerCase().search(queryValue.toLowerCase()) !== -1));
    }
    setFilterQueryJobs(tabJobs);
  };

  const handleDepartmentRemove = () => {
    setDepartment2(null);

    if (queryValue) {
      return setFilterQueryJobs(tabJobs.filter((item) => item.title.toLowerCase().search(queryValue.toLowerCase()) !== -1));
    }
    setFilterQueryJobs(tabJobs);
  };

  const handleLocationRemove = () => {
    setLocation(null);

    if (queryValue) {
      return setFilterQueryJobs(tabJobs.filter((item) => item.title.toLowerCase().search(queryValue.toLowerCase()) !== -1));
    }
    setFilterQueryJobs(tabJobs);
  };

  const jobList = propOr([], 'jobs')(data);
  const jobs = propOr([], 'jobs')(jobList);
  const changeRoute = path(['history', 'push'])(props);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newOpenJobs = jobs.filter((c) => statusIndex[0] == c.status && !c.archived);
    const newTabJobs = isArchivedTab
      ? jobs.map(mapRemoteJob)
      : jobs.filter((c) => statusIndex[selected] == c.status).map(mapRemoteJob);
    if (compareJobArrays(openJobs, newOpenJobs)) {
      setOpenJobs(newOpenJobs);
    }
    if (compareJobArrays(tabJobs, newTabJobs)) {
      setTabJobs(newTabJobs);
    }
    if (selected === 2) {
      setTabJobs(jobs.filter((c) => !c.archived));
    }
    if (isArchivedTab) {
      setTabJobs(jobs.map(mapRemoteJob));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, isArchivedTab]);

  useEffect(() => {
    if (!isNilOrEmpty(tabJobs) && !!tabJobs.map) {
      let locationsList = tabJobs.map(({ location }) => location);
      locationsList = locationsList.filter((item, index) => locationsList.indexOf(item) === index);
      setLocations(
        locationsList.map((item) => {
          return { label: item, value: item };
        })
      );
    }
    setPage(0);
  }, [tabJobs]);

  useEffect(() => {
    refetch();
    setPage(0);
  }, [selected, isArchivedTab]);

  useEffect(() => {
    setPage(0);
  }, [filterQueryJobs]);

  const toastMarkup = successToast.status ? <Toast content={successToast.message} onDismiss={toggleActive} /> : null;

  /*const createJobButton =
    isUpgradeModalOpen && openJobs.length >= subscriptionPlan.number_jobs ? (
      <Banner title='Upgrade your plan' onDismiss={() => setIsUpgradeModalOpen(false)} status='warning'>
        Please upgrade your &nbsp;
        <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
          {' '}
          <strong> subscription </strong>{' '}
        </Link>
        &nbsp; to open more jobs.
      </Banner>
    ) : null;*/

  const handleCategoryChange = (value) => {
    setCategory2(value);
    const valueLowerCase = value ? value.join(' ').toLowerCase() : '';
    const queryLowerCase = queryValue ? queryValue.toLowerCase() : '';

    if (isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (!isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ category }) => includes(category.toLowerCase(), valueLowerCase)));
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ title }) => includes(queryLowerCase, title.toLowerCase())));
    }

    setFilterQueryJobs(
      tabJobs.filter(({ category, title }) => {
        return includes(queryLowerCase, title.toLowerCase()) && includes(category.toLowerCase(), valueLowerCase);
      })
    );
  };

  const handleDepartmentChange = (value) => {
    setDepartment2(value);
    const valueLowerCase = value ? value.join(' ').toLowerCase() : '';
    const queryLowerCase = queryValue ? queryValue.toLowerCase() : '';

    if (isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (!isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(
        tabJobs.filter(({ department }) => {
          const { id = '' } = { ...department };
          return includes(id, value);
        })
      );
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ title }) => includes(queryLowerCase, title.toLowerCase())));
    }

    setFilterQueryJobs(
      tabJobs.filter(({ department, title }) => {
        const { id = '' } = { ...department };
        return includes(queryLowerCase, title.toLowerCase()) && includes(id, value);
      })
    );
  };

  const handleLocationChange = (value) => {
    setLocation(value);
    const valueLowerCase = value ? value.join(' ').toLowerCase() : '';
    const queryLowerCase = queryValue ? queryValue.toLowerCase() : '';

    if (isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs);
    }

    if (!isNilOrEmpty(value) && isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ location }) => includes(location.toLowerCase(), valueLowerCase)));
    }

    if (isNilOrEmpty(value) && !isNilOrEmpty(queryValue)) {
      return setFilterQueryJobs(tabJobs.filter(({ title }) => includes(queryLowerCase, title.toLowerCase())));
    }

    setFilterQueryJobs(
      tabJobs.filter(({ location, title }) => {
        return includes(queryLowerCase, title.toLowerCase()) && includes(location.toLowerCase(), valueLowerCase);
      })
    );
  };

  const filters = [
    {
      key: 'department',
      label: 'Department',
      filter: (
        <ChoiceList
          title='Department'
          titleHidden
          choices={departments.map(({ id: value, headingText: label }) => ({ value, label }))}
          selected={department2 || []}
          onChange={handleDepartmentChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'category',
      label: 'Category',
      filter: (
        <ChoiceList
          title='Category'
          titleHidden
          choices={categories}
          selected={category2 || []}
          onChange={handleCategoryChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
    {
      key: 'location',
      label: 'Location',
      filter: (
        <ChoiceList
          title='Location'
          titleHidden
          choices={locations}
          selected={location || []}
          onChange={handleLocationChange}
          allowMultiple
        />
      ),
      shortcut: true,
    },
  ];

  const appliedFilters = [];

  if (!isEmpty(category2)) {
    const key = 'category';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, category2),
      onRemove: handleCategoryRemove,
    });
  }

  if (!isEmpty(department2)) {
    const key = 'department';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, department2),
      onRemove: handleDepartmentRemove,
    });
  }

  if (!isEmpty(location)) {
    const key = 'location';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, location),
      onRemove: handleLocationRemove,
    });
  }

  // const handleCategoryChange = useCallback((value) => setCategory2(value), []);

  const updateStatus = async () => {
    setLoading(true);

    const response = await updateJobStatus({
      variables: {
        jobId: currentJob.id,
        shopifyToken: shopifyToken,
      },
    });

    const errors = pathOr([], ['data', 'updateStatus', 'errors'])(response);
    const emptyErrors = isEmpty(errors);
    if (emptyErrors) {
      setLoading(false);
      setActive(false);
      setSuccessToast({ status: true, message: 'Job status was updated successfully' });
      refetch();
    } else {
      setToastStatus({ status: true, message: 'There is an error with the server' });
    }
  };

  const onRefreshJob = async (jobId) => {
    try {
      setLoading(true);
      const response = await refreshJob({
        variables: {
          jobId,
          shopifyToken,
        },
      });

      const errors = pathOr([], ['data', 'refreshJob', 'errors'])(response);
      const statusOk = pathOr(false, ['data', 'refreshJob', 'status'])(response);
      const emptyErrors = isEmpty(errors);

      if (emptyErrors && !!statusOk) {
        setSuccessToast({ status: true, message: 'Job successfully updated' });
      } else {
        setSuccessToast({
          status: true,
          message: 'Unexpected error updating job, please try again',
        });
      }
    } catch (error) {
      setSuccessToast({
        status: true,
        message: 'Unexpected error updating job, please try again',
      });
    } finally {
      setLoading(false);
      refetch();
    }
  };

  const handleArchiveJob = (id) => {
    const job1 = jobs.filter((j) => j.id == id);
    setArchiveModalActive(true);
    setCurrentJob(mapRemoteJob(job1[0]));
  };

  const onArchiveJob = async () => {
    setLoading(true);

    try {
      const response = await archiveJob({
        variables: {
          jobId: currentJob.id,
          shopifyToken: shopifyToken,
        },
      });

      const errors = pathOr([], ['data', 'archiveJob', 'errors'])(response);
      const emptyErrors = isEmpty(errors);
      if (emptyErrors) {
        setSuccessToast({ status: true, message: 'Job archived successfully' });
      } else {
        setSuccessToast({ status: true, message: 'There is an error with the server' });
      }
    } catch (error) {
      setSuccessToast({ status: true, message: 'There is an error with the server' });
    } finally {
      setLoading(false);
      setArchiveModalActive(false);
      refetch();
    }
  };

  const onUnarchiveJob = async () => {
    setLoading(true);

    try {
      const response = await unarchiveJob({
        variables: {
          jobId: currentJob.id,
          shopifyToken: shopifyToken,
        },
      });

      const errors = pathOr([], ['data', 'unarchiveJob', 'errors'])(response);
      const emptyErrors = isEmpty(errors);
      if (emptyErrors) {
        setSuccessToast({ status: true, message: 'Job unarchived successfully' });
      } else {
        setSuccessToast({ status: true, message: 'There is an error with the server' });
      }
    } catch (error) {
      setSuccessToast({ status: true, message: 'There is an error with the server' });
    } finally {
      setLoading(false);
      setArchiveModalActive(false);
      refetch();
    }
  };

  const rows = filterQueryJobs || tabJobs;

  const modalMarkup = isLoading ? (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
      }}>
      <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
    </div>
  ) : (
    <TextContainer>
      <Heading>{`Are you sure you want to ${currentJob.status == 'open' ? 'close' : 'open'} this job?`}</Heading>
      <p> Job Title: {currentJob.title} </p>
      <p className='capitalize'>
        {' '}
        Category:{' '}
        {compose(
          propOr(currentJob.category, 'label'),
          find((c) => c.value == currentJob.category)
        )(categories)}{' '}
      </p>
      <p> Location: {currentJob.location} </p>
    </TextContainer>
  );

  if (!hasJobs && !loading) {
    return (
      <AppProvider>
        <Layout>
          <EmptyState
            heading='Manage your job postings'
            action={{
              content: 'Open Job',
              onAction: () => changeRoute('/set-job'),
            }}
            secondaryAction={{
              content: 'Learn more',
              url: 'https://help.shophire.co/article/6-jobs',
            }}
            image={img}>
            <p>Open jobs and start receiving applications from job seekers.</p>
          </EmptyState>
        </Layout>
      </AppProvider>
    );
  }

  const clipboardBoardSection = (status, id) => {
    return (
      <CopyToClipboard
        text={`https://${shopifyDomain}/a/careers/jobs/${id}`}
        onCopy={() => {
          setSuccessToast({
            status: true,
            message: 'Copied to clipboard',
          });
        }}>
        <span>Copy URL</span>
      </CopyToClipboard>
    );
  };

  const onRenderListItems = (item) => {
    const { id, status, title, location: jobLocation, category, statusUpdatedAt, totalCandidates, archived } = item;
    const showCategory = categories.filter((c) => c.value == category)[0];
    const { active = 0, new_candidate = 0, hired = 0 } = { ...totalCandidates };
    return (
      <ResourceList.Item id={id} accessibilityLabel={`View details for ${name}`}>
        <div className={'job-list-item'}>
          <th
            style={{
              position: 'relative',
              width: '25%',
            }}>
            <h3 className='d-flex align-items-center' onClick={() => changeRoute(`/job-candidates/${id}`)}>
              <span style={{ maxWidth: '90%' }}>
                <TextStyle variation='strong'>{title}</TextStyle>
              </span>
              <span
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}></span>
            </h3>
            <div className={'mobile-content'} style={{ position: 'absolute', top: '0vw', right: '1vh' }}>
              <Badge status={archived ? 'attention' : status == 'closed' ? 'default' : 'success'}>
                <span className='capitalize '>{archived ? 'Archived' : status}</span>
              </Badge>
            </div>
            <div>
              <span style={{ fontWeight: '300', fontSize: '12px' }}>
                {`${status === 'open' ? 'Opened' : 'Closed'} ${moment(statusUpdatedAt).format('MMMM D, YYYY')}`}
              </span>
            </div>
          </th>
          <td style={{ textTransform: 'capitalize', width: '15%' }}>
            <Tooltip content={showCategory ? showCategory.label : category} dismissOnMouseOut>
              <Link monochrome removeUnderline>
                <span className={'one__line__text'}>{showCategory ? showCategory.label : category}</span>
              </Link>
            </Tooltip>
          </td>
          <td style={{ textTransform: 'capitalize', width: '15%' }}>
            <Tooltip content={jobLocation} dismissOnMouseOut>
              <Link monochrome removeUnderline>
                <span className={'one__line__text'}>{jobLocation}</span>
              </Link>
            </Tooltip>
          </td>
          <td className={'desktop-content'} style={{ textAlign: 'center', width: '5%' }}>
            <Badge status={archived ? 'attention' : status == 'closed' ? 'default' : 'success'}>
              <span className='capitalize '>{archived ? 'Archived' : status}</span>
            </Badge>
          </td>
          <td className={'desktop-content'} style={{ textAlign: 'center', width: '10%' }}>
            {!!new_candidate ? (
              <Link onClick={() => changeRoute(`/job-candidates/${id}/new_candidate`)} removeUnderline>
                {totalCandidates.new_candidate} new
              </Link>
            ) : (
              <p>0 new</p>
            )}
          </td>
          <td className={'desktop-content'} style={{ textAlign: 'center', width: '10%' }}>
            {!!active ? (
              <Link onClick={() => changeRoute(`/job-candidates/${id}/active`)} removeUnderline>
                {totalCandidates.active} active
              </Link>
            ) : (
              <p>0 active</p>
            )}
          </td>
          <td className={'desktop-content'} style={{ textAlign: 'center', width: '10%' }}>
            {!!hired ? (
              <Link onClick={() => changeRoute(`/job-candidates/${id}/hired`)} removeUnderline>
                {totalCandidates.hired} hired
              </Link>
            ) : (
              <p>0 hired</p>
            )}
          </td>
          <td className={'counters mobile-content'}>
            {!!new_candidate ? (
              <Link onClick={() => changeRoute(`/job-candidates/${id}/new_candidate`)} removeUnderline>
                {totalCandidates.new_candidate} new
              </Link>
            ) : (
              <p>0 new</p>
            )}
            {!!active ? (
              <Link onClick={() => changeRoute(`/job-candidates/${id}/active`)} removeUnderline>
                {totalCandidates.active} active
              </Link>
            ) : (
              <p>0 active</p>
            )}
            {!!hired ? (
              <Link onClick={() => changeRoute(`/job-candidates/${id}/hired`)} removeUnderline>
                {totalCandidates.hired} hired
              </Link>
            ) : (
              <p>0 hired</p>
            )}
          </td>
          <td style={{ textAlign: 'center', width: '10%' }}>
            {archived ? (
              <Button onClick={() => handleArchiveJob(id)} fullWidth>
                Unarchive
              </Button>
            ) : (
              <Button
                onClick={() => changeRoute(`/set-job/${id}`)}
                fullWidth
                connectedDisclosure={{
                  accessibilityLabel: 'Job status',
                  disabled: openJobs.length >= subscriptionPlan.number_jobs && status == 'closed',
                  actions: [
                    {
                      content: status == 'open' ? 'View' : 'Preview',
                      onAction: () =>
                        window.open(`https://${shopifyDomain}/a/careers/jobs/${id}${status != 'open' ? '/preview' : ''}`),
                    },
                    status == 'open' && { content: clipboardBoardSection(status, id) },
                    { content: status == 'closed' ? 'Open' : 'Close', onAction: (e) => handleCurrentJob(id) },
                    { content: 'Archive', onAction: () => handleArchiveJob(id) },
                  ].filter((e) => !!e),
                }}>
                Edit
              </Button>
            )}
          </td>
        </div>
      </ResourceList.Item>
    );
  };

  const sortListOptions = [
    { label: 'Newest', value: 'DATE_MODIFIED_DESC' },
    { label: 'Oldest', value: 'DATE_MODIFIED_ASC' },
  ];

  const onSortListChange = (selected) => {
    setSortValue(selected);

    equals(selected, 'DATE_MODIFIED_DESC')
      ? compose(setFilterQueryJobs, reverse, sortBy(prop('updatedAt')))(rows)
      : compose(setFilterQueryJobs, sortBy(prop('updatedAt')))(rows);
  };

  const listResourceName = { singular: 'job', plural: 'jobs' };
  const rowsLength = rows.length;
  const counterListText = `Showing ${rowsLength} ${equals(rowsLength, 1) ? listResourceName.singular : listResourceName.plural}`;

  return (
    <AppProvider
      i18n={{
        Polaris: {
          Filters: {
            clear: 'Clear',
          },
        },
      }}>
      <Frame>
        <TitleBar title='Jobs' />
        <Page
          title='Jobs'
          subtitle={
            <Button
              external
              plain
              monochrome
              outline={false}
              icon={ViewMajorMonotone}
              url={careerPageLink}>
              View career portal
            </Button>
          }
          primaryAction={{
            content: 'Post job',
            onAction: () => changeRoute('/set-job'),
            disabled: openJobs.length >= subscriptionPlan.number_jobs,
          }}
          fullWidth>
          {openJobs.length >= subscriptionPlan.number_jobs && (
            <>
              <Banner status='warning'>
                You've reached your account plan's open job limit. To open more jobs{' '}
                <Link onClick={() => changeRoute('/account-plan')} removeUnderline>
                  <strong>upgrade your account plan</strong>
                </Link>
              </Banner>
              <br />
            </>
          )}
          <Card>
            <Tabs tabs={tabs} selected={selected} onSelect={handleTabChange}>
              <Card.Section>
                <Filters
                  queryValue={queryValue}
                  filters={filters}
                  appliedFilters={appliedFilters}
                  onQueryChange={handleFiltersQueryChange}
                  onQueryClear={handleQueryValueRemove}
                  // onClearAll={handleFiltersClearAll}
                />
              </Card.Section>
              <div className='position-relative'>
                <span className='d-sm-none resource-list__counter'>{counterListText}</span>
                {isEmpty(rows) && !isEmpty(jobs) ? (
                  <EmptyState heading='No jobs found' image={noJobsFoundImgUrl}>
                    <p>Try changing the filter or search term.</p>
                  </EmptyState>
                ) : (
                  <ResourceList
                    resourceName={listResourceName}
                    items={(rows || []).filter((el, index) => {
                      const init = page * ROWS_PER_PAGE;
                      const end = init + ROWS_PER_PAGE;
                      return index >= init && index < end;
                    })}
                    sortValue={sortValue}
                    sortOptions={sortListOptions}
                    onSortChange={onSortListChange}
                    renderItem={onRenderListItems}
                    loading={loading}
                  />
                )}
              </div>
              {Math.ceil(rowsLength / ROWS_PER_PAGE) > 0 && (
                <Card.Section>
                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', marginRight: '0.9rem' }}>
                    <Pagination
                      hasPrevious={page > 0}
                      onPrevious={() => {
                        setPage(page - 1);
                      }}
                      label={`Page ${page + 1} of ${Math.ceil(rowsLength / ROWS_PER_PAGE)}`}
                      hasNext={page * ROWS_PER_PAGE + ROWS_PER_PAGE < rowsLength}
                      onNext={() => {
                        setPage(page + 1);
                      }}
                    />
                  </div>
                </Card.Section>
              )}
            </Tabs>
          </Card>
          <br />
          {/*createJobButton*/}
          {toastMarkup}
        </Page>
        <Footer link='https://help.shophire.co/article/6-jobs' name='Jobs' />
      </Frame>
      <Modal
        open={active}
        primaryAction={{
          content: `Yes, ${currentJob.status == 'open' ? 'close' : 'open'} the job`,
          onAction: () => updateStatus(),
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleModal,
            disabled: isLoading,
          },
        ]}
        loading={false}>
        <Modal.Section>{modalMarkup}</Modal.Section>
      </Modal>
      <Modal
        open={archiveModalActive}
        primaryAction={{
          content: currentJob.archived ? 'Yes, unarchive the job' : 'Yes, archive the job',
          onAction: currentJob.archived ? onUnarchiveJob : onArchiveJob,
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleArchiveModal,
            disabled: isLoading,
          },
        ]}
        loading={false}>
        <Modal.Section>
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100vh',
              }}>
              <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
            </div>
          ) : (
            <TextContainer>
              <Heading>{`Are you sure you want to ${currentJob.archived ? 'unarchive' : 'archive'} this job?`}</Heading>
              <p>Job Title: {currentJob.title}</p>
              <p className='capitalize'>
                Category:{' '}
                {compose(
                  propOr(currentJob.category, 'label'),
                  find((c) => c.value == currentJob.category)
                )(categories)}
              </p>
              <p>Location: {currentJob.location}</p>
              {!currentJob.archived && (
                <p style={{ marginTop: '10px', color: '#637381' }}>
                  Note: Archiving a job will also close it if it's currently open.
                </p>
              )}
            </TextContainer>
          )}
        </Modal.Section>
      </Modal>
    </AppProvider>
  );

  function disambiguateLabel(key, values) {
    switch (key) {
      case 'category':
        const categories = values.map((name) => name.charAt(0).toUpperCase() + name.slice(1));
        return `Filter by ${categories.join(', ')}`;
      case 'department':
        const departmentsFilter = values
          .map((el) => departments.find(({ id }) => id === el) || {})
          .map(({ headingText }) => headingText);
        return `Filter by ${departmentsFilter.join(', ')}`;
      case 'location':
        return `Hiring location in ${(values || []).join(' - ')}`;
      default:
        return (values || []).join(', ');
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === '' || value == null;
    }
  }
};

export default JobList;
